import { Dicom } from '@/models/dicom';
import { PANEL_LAYOUTS, PanelLayout } from '@/models/settings';
import { GridLayout } from '../ViewerContext';
import { fillUnpinnedPanes } from './paneNavigation';

// Reader URL: /studies/<uuid>/<selectedClip>?layout=RxC&panes=5,6,p12,7
// Clip numbers are 1-based positions in the index-sorted DICOM list, listed in
// pane-slot order. `p` marks a pinned pane, `-` an empty one.

interface PaneToken {
  number: number;
  pinned: boolean;
}

export interface ViewerUrlState {
  selectedNumber: number;
  layout?: GridLayout;
  /** One entry per pane slot; null = empty or unreadable token. */
  panes?: (PaneToken | null)[];
}

export interface SerializedViewerUrlState {
  dicomNumber: number;
  search: string;
  /** Layout + pinned panes only — what changes without moving the clip. */
  shape: string;
}

export interface ResolvedPaneState {
  paneDicoms: (Dicom | null)[];
  pinnedSlots: ReadonlySet<number>;
  navCursor: number;
  selectedViewportIndex: number;
}

export const toGridLayout = (layout: PanelLayout): GridLayout => {
  const [rows, cols] = layout.split('x').map(Number);
  return { rows, cols };
};

export const formatGridLayout = ({ rows, cols }: GridLayout) =>
  `${rows}x${cols}` as PanelLayout;

function parseGridLayout(value: string | null): GridLayout | undefined {
  return value && (PANEL_LAYOUTS as readonly string[]).includes(value)
    ? toGridLayout(value as PanelLayout)
    : undefined;
}

function parsePaneToken(token: string): PaneToken | null {
  const match = /^(p?)(\d+)$/.exec(token);
  if (!match) return null;
  const number = Number(match[2]);
  return number >= 1 ? { number, pinned: match[1] === 'p' } : null;
}

export const formatPaneToken = (pane: PaneToken | null) =>
  pane ? `${pane.pinned ? 'p' : ''}${pane.number}` : '-';

export function parseViewerUrlState(
  dicomIndexParam: string | undefined,
  searchParams: URLSearchParams
): ViewerUrlState {
  // Out-of-range numbers pass through; the provider ignores them.
  const parsed = parseInt(dicomIndexParam ?? '1', 10);
  const selectedNumber = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
  const panesParam = searchParams.get('panes');
  return {
    selectedNumber,
    layout: parseGridLayout(searchParams.get('layout')),
    panes: panesParam ? panesParam.split(',').map(parsePaneToken) : undefined,
  };
}

function formatUrlParts(
  dicomNumber: number,
  layout: GridLayout,
  panes: (PaneToken | null)[]
): SerializedViewerUrlState {
  const format = (tokens: (PaneToken | null)[]) =>
    `layout=${formatGridLayout(layout)}&panes=${tokens.map(formatPaneToken).join(',')}`;
  return {
    dicomNumber,
    search: format(panes),
    shape: format(panes.map((p) => (p?.pinned ? p : null))),
  };
}

export function serializeViewerUrlState({
  gridLayout,
  assignedDicoms,
  pinnedPanes,
  selectedViewportIndex,
  dicomNumberByUuid,
}: {
  gridLayout: GridLayout;
  assignedDicoms: (Dicom | null)[];
  pinnedPanes: ReadonlyMap<number, Dicom>;
  selectedViewportIndex: number;
  dicomNumberByUuid: ReadonlyMap<string, number>;
}): SerializedViewerUrlState | null {
  const selected = assignedDicoms[selectedViewportIndex];
  const dicomNumber = selected && dicomNumberByUuid.get(selected.uuid);
  if (!dicomNumber) return null;
  const panes = assignedDicoms.map((d, i) => {
    const number = d && dicomNumberByUuid.get(d.uuid);
    return number ? { number, pinned: pinnedPanes.has(i) } : null;
  });
  return formatUrlParts(dicomNumber, gridLayout, panes);
}

/** Normalized form of a parsed URL, comparable to serializeViewerUrlState. */
export function formatViewerUrlState(
  state: ViewerUrlState,
  fallbackLayout: GridLayout
): SerializedViewerUrlState {
  const layout = state.layout ?? fallbackLayout;
  const panes = Array.from(
    { length: layout.rows * layout.cols },
    (_, i) => state.panes?.[i] ?? null
  );
  return formatUrlParts(state.selectedNumber, layout, panes);
}

/**
 * Panes are restored as written: pane contents don't have to be a sequential
 * run (Shift+drag, or a pane that kept its clip after an unpin), so nothing is
 * pinned that the URL doesn't pin. Navigation resumes from the first unpinned
 * pane's clip, which also fills any empty or unreadable pane.
 */
export function resolvePaneState({
  panes,
  selectedNumber,
  sortedDicoms,
  orderedDicoms,
  viewportCount,
}: {
  panes: (PaneToken | null)[];
  selectedNumber: number;
  sortedDicoms: Dicom[];
  orderedDicoms: Dicom[];
  viewportCount: number;
}): ResolvedPaneState {
  const seen = new Set<string>();
  const written = Array.from({ length: viewportCount }, (_, i) => {
    const token = panes[i];
    const dicom = token ? sortedDicoms[token.number - 1] : undefined;
    if (!dicom || seen.has(dicom.uuid)) return null;
    seen.add(dicom.uuid);
    return dicom;
  });

  const pinnedSlots = new Set<number>();
  const occupiedSlots = new Set<number>();
  written.forEach((dicom, i) => {
    if (!dicom) return;
    occupiedSlots.add(i);
    if (panes[i]?.pinned) pinnedSlots.add(i);
  });

  const anchor = written.find(
    (d, i) => d && !pinnedSlots.has(i) && orderedDicoms.includes(d)
  );
  const navCursor = anchor ? orderedDicoms.indexOf(anchor) : 0;
  const paneDicoms = fillUnpinnedPanes(
    written,
    viewportCount,
    occupiedSlots,
    orderedDicoms,
    navCursor
  );

  const selected = sortedDicoms[selectedNumber - 1];
  const selectedSlot = selected ? paneDicoms.indexOf(selected) : -1;
  return {
    paneDicoms,
    pinnedSlots,
    navCursor,
    selectedViewportIndex: Math.max(0, selectedSlot),
  };
}

export function getInitialPaneState(
  urlState: ViewerUrlState | undefined,
  sortedDicoms: Dicom[],
  orderedDicoms: Dicom[],
  viewportCount: number
): ResolvedPaneState {
  if (urlState?.panes) {
    return resolvePaneState({
      panes: urlState.panes,
      selectedNumber: urlState.selectedNumber,
      sortedDicoms,
      orderedDicoms,
      viewportCount,
    });
  }
  const pos = (urlState?.selectedNumber ?? 1) - 1;
  const position = pos >= 0 && pos < sortedDicoms.length ? pos : 0;
  const navCursor = Math.floor(position / viewportCount) * viewportCount;
  return {
    paneDicoms: fillUnpinnedPanes(
      [],
      viewportCount,
      new Set(),
      orderedDicoms,
      navCursor
    ),
    pinnedSlots: new Set(),
    navCursor,
    selectedViewportIndex: position % viewportCount,
  };
}
